import { getEnvironment } from '@toloka-tb/common/utils/environment';
import { HTTPError, type Options } from 'ky';

import { sendTimeDeltaEvent } from '../user-logs';
import { setAuthTokenHook, getDefaultAuthToken, setMondayIdHook, getMondaySubitemId } from '../utils';
import { sendTbApiRequest } from '../utils/sendTbApiRequest';

import { type CheckRequest, type CheckResponse, type TypeCheck } from './autochecker.types';
import { type LLMRequest, type LLMResponse } from './llm/llm.types';
import {
    type LLMAmazingInferenceResponses,
    type LLMAmazingRequest,
    type LLMAmazingResponse
} from './llm-amazing/llm-amazing.types';

export class CustomHTTPError extends HTTPError {
    public statusCode: number | null = null;
    public reqId: string | null = null;

    constructor(response: Response, request: Request, options: any) {
        super(response, request, options);
    }
}

const getRequestBaseURL = () => {
    return getEnvironment() === 'prod'
        ? 'https://api-ui.toloka.ai/api/autochecker/v0'
        : 'https://prestable.api-ui.toloka-test.ai/api/autochecker/v0';
};

const COMMON_REQUEST_OPTIONS: Partial<Options> = {
    credentials: 'include',
    hooks: {
        beforeRequest: [setAuthTokenHook, setMondayIdHook],
        afterResponse: [
            async (request, _options, response) => {
                if (response.status === 401) {
                    request.headers.set('Authorization', `ApiKey ${getDefaultAuthToken()}`);
                    request.headers.set('Name', 'autochecker-fallback');
                }
            }
        ],
        beforeError: [
            async (error) => {
                const { response, request, options } = error;

                const customError = new CustomHTTPError(response, request, options);

                if (!response) return customError;

                const clonedResponse = response.clone();
                let reqId: string | null = null;
                let message = error.message;

                if (clonedResponse.body) {
                    try {
                        const data = await response.clone().json();

                        reqId = response.headers.get('x-request-id');

                        if (data.detail && !reqId) reqId = '422 Unprocessable Content';
                        if (data.message) message = data.message;
                        if (data.req_id) reqId = data.req_id;
                    } catch (err) {
                        console.warn('Unable to parse request error data');
                    }
                }

                customError.reqId = reqId;
                customError.statusCode = clonedResponse.status;
                customError.message = message;

                return customError;
            }
        ]
    },
    retry: {
        limit: 3,
        methods: ['get', 'post', 'head', 'options'],
        statusCodes: [401, 408, 413, 429, 500, 502, 503, 504],
        delay: (attemptCount) => 0.5 * 2 ** (attemptCount - 1) * 1000
    },
    timeout: 600000 // 10 minutes
};

export const autochecker = {
    llm: async (data: LLMRequest, abortSignal?: AbortSignal, mondaySubitemId?: number): Promise<string> => {
        if (data.model_id === 'gpt-4-32k-0613') data.model_id = 'gpt-4o';

        const headers = new Headers();

        if (mondaySubitemId) {
            headers.set('MONDAY-SUBITEM-ID', String(mondaySubitemId));
        }

        const startTime = Date.now();
        const response = await sendTbApiRequest<LLMResponse>({
            endpointName: 'autochecker.llm',
            url: `${getRequestBaseURL()}/llm/`,
            apiChannelExtraOptions: { mondaySubitemId: mondaySubitemId ?? getMondaySubitemId() },
            method: 'POST',
            ...COMMON_REQUEST_OPTIONS,
            headers,
            json: data,
            signal: abortSignal
        });
        const endTime = Date.now();

        sendTimeDeltaEvent(`llm-request_${data.model_id}`, startTime, endTime);

        return response.answer;
    },

    llmAmazing: async (data: LLMAmazingRequest, abortSignal?: AbortSignal): Promise<LLMAmazingInferenceResponses> => {
        const startTime = Date.now();
        const response = await sendTbApiRequest<LLMAmazingResponse>({
            endpointName: 'autochecker.amazing-llm',
            url: `${getRequestBaseURL()}/amazing_llm`,
            apiChannelExtraOptions: { mondaySubitemId: getMondaySubitemId() },
            method: 'POST',
            ...COMMON_REQUEST_OPTIONS,
            json: data,
            signal: abortSignal
        });
        const endTime = Date.now();

        sendTimeDeltaEvent(`amazing-llm-request`, startTime, endTime);

        return response.inference_responses;
    },

    check: async <T extends TypeCheck, P extends CheckResponse<T>>(
        data: CheckRequest<T>,
        abortSignal?: AbortSignal,
        mondaySubitemId?: number
    ): Promise<P['result']> => {
        const startTime = Date.now();
        const checkType = data.check_type.toLowerCase();

        const headers = new Headers();

        if (mondaySubitemId) {
            headers.set('MONDAY-SUBITEM-ID', String(mondaySubitemId));
        }

        const response = await sendTbApiRequest<P>({
            endpointName: 'autochecker.check',
            url: `${getRequestBaseURL()}/check/${checkType}`,
            apiChannelExtraOptions: { mondaySubitemId: mondaySubitemId ?? getMondaySubitemId(), checkType },
            method: 'POST',
            ...COMMON_REQUEST_OPTIONS,
            headers,
            json: data.data,
            signal: abortSignal
        });
        const endTime = Date.now();

        sendTimeDeltaEvent(`check-request_${checkType.replace('_', '-')}`, startTime, endTime);

        return response.result;
    }
};
